/** HTTP 方法 */
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** 请求配置，扩展自原生 RequestInit */
export interface RequestOptions extends Omit<RequestInit, 'method' | 'body'> {
  /** 查询参数，自动拼接到 URL */
  params?: Record<string, string | number | boolean | null | undefined>;
  /** 请求体，对象会自动序列化为 JSON */
  body?: unknown;
  /** 超时时间（ms），默认 15000 */
  timeout?: number;
  /** 基础 URL，默认使用 window.location.origin */
  baseURL?: string;
}

/** 业务错误码（body.code） */
export type ApiErrorCode =
  | 'BAD_REQUEST' // 400 请求参数错误：必填参数缺失、格式错误或取值非法
  | 'UNAUTHORIZED' // 401 未授权：未携带鉴权信息或 Token 无效/已过期
  | 'FORBIDDEN' // 403 无权限：无权限访问该租户/解决方案资源
  | 'NOT_FOUND' // 404 未找到资源：tenant_code/solution_code 无匹配配置，或 chat_id/chat_record_id 不存在
  | 'TOO_MANY_REQUESTS' // 429 请求过于频繁：触发限流，需稍后重试
  | 'INTERNAL_ERROR' // 500 服务端内部错误：QueryX 或下游（SQLBot/大模型/数据源）异常
  | (string & {}); // 兼容未来新增错误码

/** HTTP 状态码与业务错误码的默认映射 */
const HTTP_STATUS_CODE_MAP: Record<number, ApiErrorCode> = {
  400: 'BAD_REQUEST',
  401: 'UNAUTHORIZED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  429: 'TOO_MANY_REQUESTS',
  500: 'INTERNAL_ERROR',
};

/** 各错误码对应的默认用户提示文案 */
const ERROR_CODE_MESSAGE: Record<string, string> = {
  BAD_REQUEST: '请求参数错误，请检查后重试',
  UNAUTHORIZED: '登录已过期，请重新登录',
  FORBIDDEN: '无权限访问该资源',
  NOT_FOUND: '请求的资源不存在',
  TOO_MANY_REQUESTS: '请求过于频繁，请稍后重试',
  INTERNAL_ERROR: '服务端异常，请稍后重试',
};

/** 业务错误响应结构 */
export interface ApiErrorData {
  code?: ApiErrorCode;
  message?: string;
  [key: string]: unknown;
}

/** 统一错误类 */
export class HttpError extends Error {
  /** HTTP 状态码，网络/超时错误为 0 */
  readonly status: number;
  /** 业务错误码（body.code） */
  readonly code: ApiErrorCode | null;
  /** 服务端返回的原始错误数据 */
  readonly data: ApiErrorData | null;

  constructor(message: string, status: number, data: ApiErrorData | null = null) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.data = data;
    this.code = data?.code ?? HTTP_STATUS_CODE_MAP[status] ?? null;
  }

  /** 是否为指定业务错误码 */
  is(code: ApiErrorCode): boolean {
    return this.code === code;
  }

  /** 是否需要重新登录（401） */
  get isUnauthorized(): boolean {
    return this.status === 401 || this.code === 'UNAUTHORIZED';
  }

  /** 是否为限流错误（429） */
  get isRateLimited(): boolean {
    return this.status === 429 || this.code === 'TOO_MANY_REQUESTS';
  }

  /** 对用户友好的提示文案（优先使用服务端 message，降级使用本地映射） */
  get userMessage(): string {
    if (this.data?.message) return this.data.message;
    if (this.code && ERROR_CODE_MESSAGE[this.code]) return ERROR_CODE_MESSAGE[this.code];
    if (this.status === 0) return this.message;
    return `请求失败（${this.status}）`;
  }
}

const DEFAULT_TIMEOUT = 60000;

/**
 * 将 params 对象拼接到 URL 查询字符串，忽略 null / undefined 值。
 */
function buildURL(path: string, baseURL: string, params?: RequestOptions['params']): string {
  const url = new URL(path.startsWith('http') ? path : `${baseURL}${path}`);
  if (params) {
    Object.entries(params).forEach(([key, val]) => {
      if (val !== null && val !== undefined) {
        url.searchParams.set(key, String(val));
      }
    });
  }
  return url.toString();
}

/**
 * 尝试将 Response 解析为 JSON，失败时返回原始文本。
 */
async function parseResponse(res: Response): Promise<unknown> {
  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    try {
      return await res.json();
    } catch {
      return null;
    }
  }
  return res.text();
}

/**
 * 核心请求函数。
 *
 * @param method  HTTP 方法
 * @param path    请求路径（绝对 URL 或相对路径）
 * @param options 请求配置
 * @returns       解析后的响应数据
 * @throws        {HttpError} 网络错误、超时或非 2xx 响应
 */
async function request<T = unknown>(
  method: HttpMethod,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const {
    params,
    body,
    timeout = DEFAULT_TIMEOUT,
    baseURL = window.location.origin,
    headers,
    signal: externalSignal,
    ...restInit
  } = options;

  const url = buildURL(path, baseURL, params);

  const controller = new AbortController();
  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort();
    } else {
      externalSignal.addEventListener('abort', () => controller.abort(), { once: true });
    }
  }
  const timer = setTimeout(() => controller.abort(), timeout);

  const init: RequestInit = {
    ...restInit,
    method,
    signal: controller.signal,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  };

  if (body !== undefined && method !== 'GET' && method !== 'DELETE') {
    init.body = JSON.stringify(body);
  }

  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (err) {
    clearTimeout(timer);
    if (err instanceof DOMException && err.name === 'AbortError') {
      if (externalSignal?.aborted) {
        throw new HttpError('请求已取消', 0);
      }
      throw new HttpError(`请求超时（>${timeout}ms）：${url}`, 0);
    }
    throw new HttpError(`网络错误：${(err as Error).message}`, 0);
  }
  clearTimeout(timer);

  const data = await parseResponse(res);

  if (!res.ok) {
    const errorData = (
      typeof data === 'object' && data !== null ? data : { message: data }
    ) as ApiErrorData;
    const message = errorData.message ?? `HTTP ${res.status} ${res.statusText}`;
    throw new HttpError(String(message), res.status, errorData);
  }

  return data as T;
}

/** 便捷方法集合 */
export const http = {
  get: <T = unknown>(path: string, options?: Omit<RequestOptions, 'body'>) =>
    request<T>('GET', path, options),

  post: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, { ...options, body }),

  put: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, { ...options, body }),

  patch: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, { ...options, body }),

  delete: <T = unknown>(path: string, options?: RequestOptions) =>
    request<T>('DELETE', path, options),
};
